import type { DataConnection, Peer, PeerErrorType, PeerJSOption } from 'peerjs'
import type { ComputedRef, Ref } from 'vue'

import type { ChallengeWheelSyncState } from '~/stores/challengeWheel'

import { controllerPeerIdForCode } from '~/utils/pairingCode'

export type PeerTransportMessage
  = | ControllerDisconnectMessage
    | PresenterReadyMessage
    | SyncStateMessage

export interface PeerTransportOptions {
  onControllerDisconnect?: () => void
  onPeerConnected?: () => void
  onPeerDisconnected?: () => void
  onStateReceived?: (state: ChallengeWheelSyncState) => void
  onStatusMessage?: (status: string) => void
  role: ComputedRef<'controller' | 'presenter'>
  // Accept either a ref or computed — ComputedRef technically extends Ref in
  // Vue's types, but spelling the union keeps it obvious to callers.
  targetCode: ComputedRef<string> | Ref<string>
}

interface ControllerDisconnectMessage {
  type: 'controller-disconnect'
}

interface PresenterReadyMessage {
  type: 'presenter-ready'
}

interface SyncStateMessage {
  state: ChallengeWheelSyncState
  type: 'sync-state'
}

// Wait before retrying a failed presenter-side connection. Short enough that a
// brief signaling-server blip is invisible, long enough that we don't spam the
// PeerJS cloud server during a sustained outage.
const PRESENTER_RECONNECT_MIN_DELAY_MS = 800
const PRESENTER_RECONNECT_MAX_DELAY_MS = 8000

// PeerJS error codes we recognize. The library ships them as the
// `PeerErrorType` enum but we string-compare to keep runtime deps minimal.
const ERR_UNAVAILABLE_ID: PeerErrorType = 'unavailable-id' as PeerErrorType
const ERR_PEER_UNAVAILABLE: PeerErrorType = 'peer-unavailable' as PeerErrorType

export function useChallengeWheelPeerTransport(options: PeerTransportOptions) {
  const { role, targetCode } = options

  const peer = shallowRef<null | Peer>(null)
  const activeConnections = shallowRef<Set<DataConnection>>(new Set())
  const presenterConnection = shallowRef<DataConnection | null>(null)
  const connectedCount = ref(0)
  const isReady = ref(false)
  const isConnecting = ref(false)
  const lastError = ref<null | string>(null)

  let peerModulePromise: null | Promise<typeof import('peerjs')> = null
  let reconnectTimer: null | ReturnType<typeof setTimeout> = null
  let reconnectDelay = PRESENTER_RECONNECT_MIN_DELAY_MS
  let intendedClose = false

  function loadPeerModule() {
    if (!peerModulePromise) {
      peerModulePromise = import('peerjs')
    }
    return peerModulePromise
  }

  function refreshConnectedCount() {
    let active = 0
    for (const conn of activeConnections.value) {
      if (conn.open) {
        active++
      }
    }
    connectedCount.value = active
  }

  function broadcastAsController(message: PeerTransportMessage) {
    if (role.value !== 'controller') {
      return
    }
    for (const conn of activeConnections.value) {
      if (conn.open) {
        try {
          conn.send(message)
        } catch {
          // Connection may have closed between the open check and send; drop.
        }
      }
    }
  }

  function sendAsPresenter(message: PeerTransportMessage) {
    if (role.value !== 'presenter') {
      return
    }
    const conn = presenterConnection.value
    if (!conn || !conn.open) {
      return
    }
    try {
      conn.send(message)
    } catch {
      // Connection dropped mid-send; reconnect loop will kick in.
    }
  }

  function send(message: PeerTransportMessage) {
    if (role.value === 'controller') {
      broadcastAsController(message)
    } else {
      sendAsPresenter(message)
    }
  }

  function wireControllerConnection(conn: DataConnection) {
    activeConnections.value.add(conn)
    conn.on('open', () => {
      refreshConnectedCount()
      options.onPeerConnected?.()
    })
    conn.on('data', rawData => {
      const message = parseMessage(rawData)
      if (!message) {
        return
      }
      if (message.type === 'presenter-ready') {
        // Controller handler bootstraps the presenter with current state; the
        // orchestrator takes over broadcasting from there.
        options.onPeerConnected?.()
      }
    })
    conn.on('close', () => {
      activeConnections.value.delete(conn)
      refreshConnectedCount()
      options.onPeerDisconnected?.()
    })
    conn.on('error', () => {
      activeConnections.value.delete(conn)
      refreshConnectedCount()
    })
  }

  function wirePresenterConnection(conn: DataConnection) {
    presenterConnection.value = conn
    conn.on('open', () => {
      reconnectDelay = PRESENTER_RECONNECT_MIN_DELAY_MS
      isConnecting.value = false
      lastError.value = null
      // Signal readiness so the controller can send us the current state.
      try {
        conn.send({ type: 'presenter-ready' } satisfies PresenterReadyMessage)
      } catch {
        // Connection failed before we could send; reconnect loop handles it.
      }
      options.onPeerConnected?.()
    })
    conn.on('data', rawData => {
      const message = parseMessage(rawData)
      if (!message) {
        return
      }
      if (message.type === 'sync-state') {
        options.onStateReceived?.(message.state)
      } else if (message.type === 'controller-disconnect') {
        options.onControllerDisconnect?.()
      }
    })
    conn.on('close', () => {
      if (presenterConnection.value === conn) {
        presenterConnection.value = null
      }
      options.onPeerDisconnected?.()
      if (!intendedClose) {
        schedulePresenterReconnect()
      }
    })
    conn.on('error', () => {
      // Errors are usually followed by close; reconnect is handled there.
    })
  }

  function clearReconnectTimer() {
    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
  }

  function schedulePresenterReconnect() {
    if (intendedClose || role.value !== 'presenter') {
      return
    }
    clearReconnectTimer()
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null
      void connectToController()
    }, reconnectDelay)
    reconnectDelay = Math.min(reconnectDelay * 2, PRESENTER_RECONNECT_MAX_DELAY_MS)
  }

  async function ensurePeer(desiredId?: string): Promise<null | Peer> {
    if (!import.meta.client) {
      return null
    }

    if (peer.value && !peer.value.destroyed) {
      if (!desiredId || peer.value.id === desiredId) {
        // Wait for an in-flight registration to complete — otherwise the caller
        // gets a Peer that hasn't yet opened its signaling socket, and any
        // immediate .connect() call silently misses.
        if (!peer.value.open) {
          await waitForPeerOpen(peer.value)
        }
        return peer.value
      }
      // Need to re-register under a new ID — destroy and recreate.
      destroyPeer()
    }

    const { Peer: PeerCtor } = await loadPeerModule()
    const instance = desiredId
      ? new PeerCtor(desiredId, defaultPeerOptions())
      : new PeerCtor(defaultPeerOptions())

    peer.value = instance
    wirePeer(instance)
    await waitForPeerOpen(instance)
    return instance
  }

  function waitForPeerOpen(instance: Peer): Promise<void> {
    if (instance.open) {
      return Promise.resolve()
    }
    return new Promise(resolve => {
      const finish = () => {
        instance.off('open', finish)
        instance.off('error', finish)
        resolve()
      }
      instance.on('open', finish)
      instance.on('error', finish)
    })
  }

  function wirePeer(instance: Peer) {
    instance.on('open', () => {
      isReady.value = true
      lastError.value = null
      reconnectDelay = PRESENTER_RECONNECT_MIN_DELAY_MS
      options.onStatusMessage?.('Signaling server connected.')
    })

    instance.on('disconnected', () => {
      isReady.value = false
      if (intendedClose || instance.destroyed) {
        return
      }
      // Lost the signaling socket — PeerJS keeps our ID reserved briefly, so
      // reconnect() is the right move here (not a fresh Peer).
      try {
        instance.reconnect()
      } catch {
        destroyPeer()
      }
    })

    instance.on('close', () => {
      isReady.value = false
    })

    if (role.value === 'controller') {
      instance.on('connection', conn => {
        wireControllerConnection(conn)
      })
    }

    instance.on('error', err => {
      if (err.type === ERR_UNAVAILABLE_ID) {
        lastError.value = 'This pairing code is already in use. Generating a new one.'
        options.onStatusMessage?.(lastError.value)
        destroyPeer()
        isReady.value = false
        return
      }

      if (err.type === ERR_PEER_UNAVAILABLE) {
        // Controller isn't online yet — keep retrying via the reconnect loop.
        isConnecting.value = false
        schedulePresenterReconnect()
        return
      }

      // Other errors: log and allow reconnect logic to handle.
      lastError.value = err.message || 'WebRTC signaling error.'
      isReady.value = false
      schedulePresenterReconnect()
    })
  }

  function destroyPeer() {
    const instance = peer.value
    peer.value = null
    isReady.value = false
    for (const conn of activeConnections.value) {
      try {
        conn.close()
      } catch {
        // Already closed.
      }
    }
    activeConnections.value.clear()
    refreshConnectedCount()
    if (presenterConnection.value) {
      try {
        presenterConnection.value.close()
      } catch {
        // Ignore.
      }
      presenterConnection.value = null
    }
    if (instance && !instance.destroyed) {
      try {
        instance.destroy()
      } catch {
        // Already destroyed.
      }
    }
  }

  async function startController(): Promise<boolean> {
    if (!import.meta.client || role.value !== 'controller') {
      return false
    }
    const code = targetCode.value
    if (!code) {
      return false
    }
    const desiredId = controllerPeerIdForCode(code)
    try {
      await ensurePeer(desiredId)
      return true
    } catch (error: unknown) {
      lastError.value = error instanceof Error ? error.message : 'Failed to start WebRTC peer.'
      return false
    }
  }

  async function connectToController(): Promise<boolean> {
    if (!import.meta.client || role.value !== 'presenter') {
      return false
    }
    const code = targetCode.value
    if (!code) {
      return false
    }

    // Deduplicate: if a dial is already in flight or the channel is open, skip.
    if (presenterConnection.value) {
      const existing = presenterConnection.value
      if (existing.open || existing.peer === controllerPeerIdForCode(code)) {
        return true
      }
    }
    if (isConnecting.value) {
      return true
    }

    isConnecting.value = true
    intendedClose = false

    try {
      const instance = await ensurePeer()
      if (!instance) {
        return false
      }
      const targetId = controllerPeerIdForCode(code)
      const conn = instance.connect(targetId, { reliable: true, serialization: 'json' })
      wirePresenterConnection(conn)
      return true
    } catch (error: unknown) {
      lastError.value = error instanceof Error ? error.message : 'Failed to connect via WebRTC.'
      schedulePresenterReconnect()
      return false
    }
  }

  function stop() {
    intendedClose = true
    clearReconnectTimer()
    destroyPeer()
  }

  function parseMessage(payload: unknown): null | PeerTransportMessage {
    if (!payload || typeof payload !== 'object') {
      return null
    }
    const type = (payload as { type?: unknown }).type
    if (type === 'presenter-ready') {
      return { type: 'presenter-ready' }
    }
    if (type === 'controller-disconnect') {
      return { type: 'controller-disconnect' }
    }
    if (type === 'sync-state') {
      const state = (payload as { state?: ChallengeWheelSyncState }).state
      if (!state) {
        return null
      }
      return { state, type: 'sync-state' }
    }
    return null
  }

  // Pre-warm the peerjs module import in the background so the first pairing
  // action doesn't stall on a ~50 KB dynamic chunk.
  if (import.meta.client) {
    void loadPeerModule()
  }

  return {
    activeConnections,
    connectedCount,
    connectToController,
    isConnecting,
    isReady,
    lastError,
    send,
    startController,
    stop,
  }
}

function defaultPeerOptions(): PeerJSOption {
  // Default PeerJS cloud signaling server. STUN defaults to Google's public
  // servers; we intentionally omit TURN here — corporate/strict NAT users can
  // be added later if needed.
  return {
    debug: 0,
  }
}
